import {
  DragDirection,
  ScrollStyle,
} from "@brexhq/next/src/components/modules/CarouselSwipeCards/types";
import { LeadRoutingEventType } from "@brexhq/next/src/components/modules/FormMultistep/types";
import { LeadFormSteps } from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import { VoteType } from "@brexhq/next/src/components/shared/HelpCenter/ArticleFeedback/ArticleFeedback";
import type { SignedupUserProperties } from "@brexhq/next/src/utils/signup";
import { SIGNUP_STEPS } from "@brexhq/next/src/utils/signup";
import { LeadFormStatus, SalesSegment } from "@brexhq/next/types/brex-api";

import { FormValues } from "../components/building-blocks/MarketoForm/types";
import { LeadFormValues } from "../components/modules/LeadForm/sharedTypes";

export enum EventNames {
  SIGNUP_FIELD_BLURRED = "Signup Field Blurred",
  SIGNUP_FIELD_FOCUSED = "Signup Field Focused",
  SIGNUP_FIELD_DIRTIED = "Signup Field Dirtied",
  /**
   * TODO: Deprecate "MARKETING"
   */
  MARKETING_FIELD_FOCUSED = "Marketing Field Focused",
  MARKETING_FIELD_BLURRED = "Marketing Field Blurred",
  MARKETING_FORM_SUBMITTED = "Marketing Form Submitted",
  /**
   * TODO: Replace all MARKETING events with LEAD
   */
  LEAD_FORM_VIEWED = "Lead Form Viewed",
  LEAD_FORM_SUBMITTED = "Lead Form Submitted",
  LEAD_FORM_FIELD_FOCUSED = "Lead Form Field Focused",
  LEAD_FORM_FIELD_BLURRED = "Lead Form Field Blurred",
  LEAD_CREATED = "Lead Created",
  LEAD_UPDATED = "Lead Updated",

  /**
   * External offline events
   */
  EVENT_PAGE_VIEWED = "Event Page Viewed",

  SIGNUP_USER_INFO_SUBMITTED = "Signup User Info Submitted",
  WEB_VITALS = "Web Vitals",
  PAGE_NOT_FOUND = "Page Not Found",
  NONE = "None",
  ERROR = "Error page shown",
  EXPERIMENT_VIEWED = "Experiment Viewed",
  EXPERIMENT_MODULE_IN_VIEW = "Experiment Module In View",
  FOOTER_VIEWED = "Footer Viewed",
  APPLICATION_RENDERED = "Application Rendered",
  APPLICATION_LAUNCHED = "Application Launched",
  INTERNAL_LINK_CLICKED = "Click: Link",
  BUTTON_CLICKED = "Click: Button",
  OUTBOUND_LINK_CLICKED = "Click: Outbound Link",
  PAGE_VIEWED = "Page Viewed",
  ANNOUNCEMENT_BANNER_DISMISSED = "Announcement Banner Dismissed",
  ANNOUNCEMENT_BANNER_SHOWN = "Announcement Banner Shown",
  FORM_SUBMITTED = "Form Submitted",
  FIELD_DIRTIED = "Field Dirtied",
  FILTER_SELECTED = "Filter Selected",
  FILTER_CLEARED = "Filter Cleared",
  PRODUCT_LOG_PINNED_POST_CLICKED = "Product Log Pinned Post Clicked",
  PRODUCT_LOG_SEARCH_QUERY_CHANGED = "Product Log Search Query Changed",
  PRODUCT_LOG_PAGE_CHANGE = "Product Log Page Change",
  SSU_STEP_SUBMITTED = "SSU Step Submitted",
  SSU_STEP_VIEWED = "SSU Step Viewed",
  CAROUSEL_CARD_CHANGED = "Carousel Card Changed",
  INDUSTRY_SEARCH_SENT = "Industry Search Sent",
  INDUSTRY_SEARCH_COMPLETED = "Industry Search Completed",
  INDUSTRY_SEARCH_SELECTED = "Industry Search Selected",
  INDUSTRY_FORM_SUBMITTED = "Industry Form Submitted",
  SOFT_SIGNUP = "user.soft_signup",
  EARLY_ACCOUNT_SIGNUP = "user.early_account_signup",
  ARTICLE_FEEDBACK_SUBMITTED = "Article Feedback Submitted",
  ARTICLE_NEGATIVE_FEEDBACK_FORM_SUBMITTED = "Article Negative Feedback Form Submitted",
  SUPPORT_CHAT_TRIGGERED = "Support Chat Triggered",
  SUPPORT_CHAT_OPENED = "Support Chat Opened",
  SUPPORT_CHAT_CLOSED = "Support Chat Closed",
  HELPSHIFT_OPENED = "Helpshift Opened",
  MAINTENANCE_SCREEN_VIEWED = "Maintenance Screen Viewed",
  INVESTMENT_SOURCE_MULTISELECT = "Investment Source Multi Submitted",
  HELP_ARTICLE_VIEWED = "Help Article Viewed",
  HELP_ARTICLE_SEARCH_RESULT_VIEWED = "Help Article Search Result Viewed",
  HELP_ARTICLE_SEARCH_RESULT_CLICKED = "Help Article Search Result Clicked",
  FORM_STEP_VIEWED = "Form Step Viewed",
  LEAD_ROUTING_EVENT = "Lead Routing Event",
  ONBOARDING_EXPERIMENT_VIEWED = "onboarding.experiment.viewed",
  SCROLLING_SUBNAV_ROW_VIEWED = "Scrolling Sub Nav Row Viewed",

  /**Content hub search / filter */
  SHOW_MORE_ARTICLES = "Show More Articles clicked",
  NO_RESULTS = "No Results View Shown",
  OPEN_SEARCH_RESULTS = "Opened Search Results",
  OPEN_ARTICLE = "Clicked article from search results",
  TOGGLED_FILTERS = "Toggled Filters",

  PAGE_SKELETON_SHOWN = "Page Skeleton Shown",

  /** One Click Sign In */
  ONE_CLICK_SIGN_IN_OPT_IN = "One Click Sign In Opt In",
  ONE_CLICK_SIGN_IN_OPT_OUT = "One Click Sign In Opt Out",
  ONE_CLICK_SIGN_IN_REDIRECT = "One Click Sign In Redirect",
  ONE_CLICK_SIGN_IN_DIALOG_DISPLAYED = "One Click Sign In Dialog Displayed",
}

export class ButtonClickedEvent {
  public readonly value = EventNames.BUTTON_CLICKED;
  public readonly properties: {
    /**
     * Combined properties
     */
    target: string;

    /**
     * Parent component of where this event is dispatched from
     */
    component: string;

    /**
     * The section of the parent component where this event is dispatched from
     */
    section: string;

    /**
     * The style of a component i.e. primary for buttons
     */
    style: string;

    /**
     * The text content of the button
     */
    text: string;
  };
  readonly experimentId?: string;

  constructor(component: string, section: string, style: string, text: string) {
    const parts = [component, section, style, text];
    const target = parts.filter((part) => part).join(".");

    this.properties = {
      target,
      component,
      section,
      style,
      text,
    };
  }
}

export class InternalLinkClickedEvent {
  static linkClicked(
    componentName: string,
    section: string,
    text = "",
    style = "",
  ) {
    return new InternalLinkClickedEvent(componentName, section, style, text);
  }

  public static heroClicked(text = "Open an account") {
    return new InternalLinkClickedEvent("hero", "cta", "primary", text);
  }

  public readonly value = EventNames.INTERNAL_LINK_CLICKED;
  public readonly properties: {
    /**
     * Combined properties
     */
    target: string;

    /**
     * Parent component of where this event is dispatched from
     */
    component: string;

    /**
     * The section of the parent component where this event is dispatched from
     */
    section: string;

    /**
     * The style of a component i.e. primary for buttons
     */
    style: string;

    /**
     * The text content of the link/button
     */
    text: string;

    /**
     * The path that the link is targeting
     */
    targetPath?: string;

    /**
     * Information about the module on the page.
     * format of moduleType.moduleNumberOnPage
     */
    moduleInformation?: string;

    /**
     * Target ID used for analytics
     */
    trackingId?: string;
  };
  readonly experimentId?: string;

  constructor(
    component: string,
    section: string,
    style: string,
    text: string,
    targetPath?: string,
    moduleType?: string,
    moduleNumberOnPage?: number,
    trackingId?: string,
  ) {
    const parts = [component, section, style, text];
    const target = parts.filter((part) => part).join(".");
    const moduleInformation = `${moduleType}.${moduleNumberOnPage}`;

    this.properties = {
      target,
      component,
      section,
      style,
      text,
      targetPath,
      moduleInformation,
      trackingId,
    };
  }
}

export class OutboundLinkClickedEvent {
  public static headerSigninClicked() {
    return new OutboundLinkClickedEvent("https://dashboard.brex.com");
  }
  public readonly value = EventNames.OUTBOUND_LINK_CLICKED;
  public readonly properties: {
    url: string;
  };
  readonly experimentId?: string;

  constructor(url: string) {
    this.properties = {
      url,
    };
  }
}

/**
 * Help Center Search Article Result Viewed
 */

export class HelpCenterArticleViewedEvent {
  readonly value = EventNames.HELP_ARTICLE_VIEWED;
  readonly properties: {
    /**
     * Internal Taxonomy Customer Experience Category
     */
    internalCategory: string;
    /**
     * List of Internal Taxonomy Customer Experience Sub Categories
     */
    internalSubcategories: string[];
    /**
     * The session source (e.g. mobile)
     */
    source?: string;
  };
  readonly experimentId?: string;
  constructor(
    internalCategory: string,
    internalSubcategories: string[],
    source?: string,
  ) {
    this.properties = {
      internalCategory,
      internalSubcategories,
      source,
    };
  }
}

/**
 * Help Center Search Article Result Clicked
 */

export class HelpCenterSearchResultClickedEvent {
  readonly value = EventNames.HELP_ARTICLE_SEARCH_RESULT_CLICKED;
  readonly properties: {
    index: string;
    eventType: string;
    queryID: string;
    objectID: string;
    position: number;
    url: string;
    title: string;
  };
  readonly experimentId?: string;
  constructor(
    index: string,
    queryID: string,
    objectID: string,
    position: number,
    url: string,
    title: string,
  ) {
    this.properties = {
      index,
      eventType: "clicked",
      queryID,
      objectID,
      position,
      url,
      title,
    };
  }
}

/**
 * Help Center Search Result Viewed
 */
export class HelpCenterSearchResultViewedEvent {
  readonly value = EventNames.HELP_ARTICLE_SEARCH_RESULT_VIEWED;
  readonly properties: {
    index: string;
    eventType: string;
    queryID: string;
    objectIDs: string[];
    positions: number[];
    titles: string[];
    urls: string[];
  };
  readonly experimentId?: string;
  constructor(
    index: string,
    queryID: string,
    objectIDs: string[],
    positions: number[],
    titles: string[],
    urls: string[],
  ) {
    this.properties = {
      eventType: "view",
      index,
      queryID,
      objectIDs,
      positions,
      titles,
      urls,
    };
  }
}

/**
 * Help Center Article Up/Down vote
 */
export class ArticleFeedbackEvent {
  readonly value = EventNames.ARTICLE_FEEDBACK_SUBMITTED;
  readonly properties: {
    /**
     * Slug of the page with up/down vote
     */
    slug: string;
    /**
     * positive or negative vote
     */
    vote: VoteType;
  };
  readonly experimentId?: string;
  constructor(slug: string, vote: VoteType) {
    this.properties = { slug, vote };
  }
}

/**
 * Help Center Article Form shown after a down vote
 */
export class ArticleNegativeFeedbackFormEvent {
  readonly value = EventNames.ARTICLE_NEGATIVE_FEEDBACK_FORM_SUBMITTED;
  readonly properties: {
    /**
     * Slug of the page with up/down vote
     */
    slug: string;
    /**
     * Response to the "why" field
     */
    why: string;
    /**
     * Response to the "tell us more" field
     */
    comments: string;
  };
  readonly experimentId?: string;
  constructor(slug: string, why: string, comments: string) {
    this.properties = { slug, why, comments };
  }
}

/** Support chat was triggered and will load */
export class SupportChatTriggeredEvent {
  readonly properties = {};
  readonly value = EventNames.SUPPORT_CHAT_TRIGGERED;
}

/** Support chat opened */
export class SupportChatOpenedEvent {
  readonly properties = {};
  readonly value = EventNames.SUPPORT_CHAT_OPENED;
}

/** Support chat closed */
export class SupportChatClosedEvent {
  readonly properties = {};
  readonly value = EventNames.SUPPORT_CHAT_CLOSED;
}

/**
 * MARKETING form field focussed
 */
export class MarketingFieldFocusedEvent {
  readonly value = EventNames.MARKETING_FIELD_FOCUSED;
  readonly properties: {
    /**
     * Name of form field
     */
    fieldName: string;
    formId: number;
  };

  readonly experimentId?: string;

  constructor(fieldName: string, formId: number) {
    this.properties = {
      fieldName,
      formId,
    };
  }
}

/**
 * User either blurred or focussed a form field
 */
export class MarketingFieldBlurredEvent {
  readonly value = EventNames.MARKETING_FIELD_BLURRED;
  readonly properties: {
    /**
     * Name of form field
     */
    fieldName: string;
    formId: number;
  };

  readonly experimentId?: string;

  constructor(fieldName: string, formId: number) {
    this.properties = {
      fieldName,
      formId,
    };
  }
}

/**
 * User shown error component
 */

type ErrorComponentSource = "Error Boundary" | "Error Page";
export class ErrorComponentShown {
  readonly value = EventNames.ERROR;

  readonly properties: {
    statusCode: number | "";
    err: string;
    source: ErrorComponentSource;
    metaData?: any;
  };

  readonly experimentId?: string;

  constructor(
    statusCode: number | "",
    err: string,
    source: ErrorComponentSource,
    metaData?: any,
  ) {
    this.properties = {
      statusCode,
      err,
      source,
      metaData: metaData ?? {},
    };
  }
}

/**
 * User shown error component
 */

export class PageLoadingSkeletonShown {
  readonly value = EventNames.PAGE_SKELETON_SHOWN;

  readonly properties: {
    path?: string;
  };

  readonly experimentId?: string;

  constructor(path?: string) {
    this.properties = {
      path,
    };
  }
}

/**
 * User submitted a form eg inline ssu
 */
export class FormSubmittedEvent {
  readonly value = EventNames.FORM_SUBMITTED;
  readonly properties: {
    /**
     * Name of form
     */
    formName: string;
    /**
     * Name of the company
     */
    companyName?: LeadFormValues["companyName"];
    /**
     * Website of the company
     */
    website?: LeadFormValues["website"];
    /**
     * Sales volume of the company
     */
    monthlySales?: LeadFormValues["monthlySales"];
    /**
     * Employee count of the company
     */
    employeeCount?: LeadFormValues["employeeCount"];
    /**
     * Funding sources of the company
     */
    fundingSources?: LeadFormValues["fundingSources"];
  };

  readonly experimentId?: string;

  constructor(formName: string, values?: LeadFormValues) {
    this.properties = {
      formName,
      companyName: values?.companyName,
      website: values?.website,
      monthlySales: values?.monthlySales,
      employeeCount: values?.employeeCount,
      fundingSources: values?.fundingSources,
    };
  }
}

/**
 * User submitted a Marketing form
 */
export class MarketingFormSubmittedEvent {
  readonly value = EventNames.MARKETING_FORM_SUBMITTED;
  readonly properties: {
    /**
     * Name of Marketo form program
     */
    programName: string;
    formId?: number;
    formValues?: FormValues;
  };

  readonly experimentId?: string;

  constructor(
    programName: string,
    formValues?: FormValues & { formId: number },
  ) {
    this.properties = {
      programName,
      formId: formValues?.formId,
      formValues,
    };
  }
}
/**
 * User submitted a Lead form
 */
export class LeadFormSubmittedEvent {
  readonly value = EventNames.LEAD_FORM_SUBMITTED;
  readonly properties: {
    /**
     * Name of step
     */
    step: LeadFormSteps;
    /**
     * Name of Marketo form program
     */
    programName: string;
    /**
     * Name of the company
     */
    companyName?: LeadFormValues["companyName"];
    /**
     * Website of the company
     */
    website?: LeadFormValues["website"];
    /**
     * Sales volume of the company
     */
    monthlySales?: LeadFormValues["monthlySales"];
    /**
     * Employee count of the company
     */
    employeeCount?: LeadFormValues["employeeCount"];
    /**
     * Funding sources of the company
     */
    fundingSources?: LeadFormValues["fundingSources"];
  };
  readonly experimentId?: string;
  constructor(
    step: LeadFormSteps,
    programName: string,
    values?: LeadFormValues,
  ) {
    this.properties = {
      step,
      programName,
      companyName: values?.companyName,
      website: values?.website,
      monthlySales: values?.monthlySales,
      employeeCount: values?.employeeCount,
      fundingSources: values?.fundingSources,
    };
  }
}

/**
 * User viewed a Lead form
 */
export class LeadFormViewedEvent {
  readonly value = EventNames.SSU_STEP_VIEWED;
  readonly properties: {
    /**
     * Name of step
     */
    step: LeadFormSteps;
    /**
     * Name of Marketo form program
     */
    programName: string;
  };
  readonly experimentId?: string;
  constructor(step: LeadFormSteps, programName: string) {
    this.properties = { step, programName };
  }
}

/**
 * Lead is created
 */

export class LeadCreatedEvent {
  readonly value = EventNames.LEAD_CREATED;
  readonly properties: {
    /**
     * lead status
     */
    status: LeadFormStatus;
    /**
     * Sales segment
     */
    salesSegment?: SalesSegment;
    /**
     * Name of the company
     */
    companyName?: LeadFormValues["companyName"];
    /**
     * Website of the company
     */
    website?: LeadFormValues["website"];
    /**
     * Sales volume of the company
     */
    monthlySales?: LeadFormValues["monthlySales"];
    /**
     * Employee count of the company
     */
    employeeCount?: LeadFormValues["employeeCount"];
    /**
     * Funding sources of the company
     */
    fundingSources?: LeadFormValues["fundingSources"];
  };

  readonly experimentId?: string;
  constructor(
    status: LeadFormStatus,
    leadProperties?: {
      companyName?: LeadFormValues["companyName"];
      website?: LeadFormValues["website"];
      monthlySales?: LeadFormValues["monthlySales"];
      employeeCount?: LeadFormValues["employeeCount"];
      fundingSources?: LeadFormValues["fundingSources"];
      salesSegment?: SalesSegment;
    },
  ) {
    this.properties = { status, ...(leadProperties ?? {}) };
  }
}

/**
 * Lead is updated
 */

export class LeadUpdatedEvent {
  readonly value = EventNames.LEAD_UPDATED;
  readonly properties: {
    /**
     * lead status
     */
    status: LeadFormStatus;
    /**
     * Sales segment
     */
    salesSegment?: SalesSegment;
    /**
     * Name of the company
     */
    companyName?: LeadFormValues["companyName"];
    /**
     * Website of the company
     */
    website?: LeadFormValues["website"];
    /**
     * Sales volume of the company
     */
    monthlySales?: LeadFormValues["monthlySales"];
    /**
     * Employee count of the company
     */
    employeeCount?: LeadFormValues["employeeCount"];
    /**
     * Funding sources of the company
     */
    fundingSources?: LeadFormValues["fundingSources"];
  };

  readonly experimentId?: string;
  constructor(
    status: LeadFormStatus,
    leadProperties?: {
      companyName?: LeadFormValues["companyName"];
      website?: LeadFormValues["website"];
      monthlySales?: LeadFormValues["monthlySales"];
      employeeCount?: LeadFormValues["employeeCount"];
      fundingSources?: LeadFormValues["fundingSources"];
      salesSegment?: SalesSegment;
    },
  ) {
    this.properties = { status, ...(leadProperties ?? {}) };
  }
}

/**
 * User either blurred or focused a form field
 */
export class LeadFormBlurredEvent {
  readonly value = EventNames.LEAD_FORM_FIELD_BLURRED;
  readonly properties: {
    /**
     * Name of form field
     */
    fieldName: string;
    /**
     * Name of Marketo form program
     */
    programName: string;
  };

  readonly experimentId?: string;

  constructor(fieldName: string, programName: string) {
    this.properties = {
      fieldName,
      programName,
    };
  }
}

/**
 * Lead form field focussed
 */
export class LeadFormFocusedEvent {
  readonly value = EventNames.LEAD_FORM_FIELD_BLURRED;
  readonly properties: {
    /**
     * Name of form field
     */
    fieldName: string;
    /**
     * Name of Marketo form program
     */
    programName: string;
  };

  readonly experimentId?: string;

  constructor(fieldName: string, programName: string) {
    this.properties = {
      fieldName,
      programName,
    };
  }
}

/**
 * Signup form field focussed
 */
export class SignupFieldFocusedEvent {
  readonly value = EventNames.SIGNUP_FIELD_FOCUSED;
  readonly properties: {
    /**
     * Name of form field
     */
    field: string;

    /**
     * Whether or not the user has entered info
     */
    isDirty: boolean;
  };

  readonly experimentId?: string;

  constructor(field: string, isDirty: boolean) {
    this.properties = { field, isDirty };
  }
}

/**
 * User either blurred or focussed a form field
 */
export class SignupFieldBlurredEvent {
  readonly value = EventNames.SIGNUP_FIELD_BLURRED;
  readonly properties: {
    /**
     * Name of form field
     */
    field: string;

    /**
     * Whether or not the user has entered info
     */
    isDirty: boolean;
  };
  readonly experimentId?: string;

  constructor(field: string, isDirty: boolean) {
    this.properties = {
      field,
      isDirty,
    };
  }
}

/**
 * User has edited a form field
 */
export class SignupFieldDirtiedEvent {
  readonly value = EventNames.SIGNUP_FIELD_DIRTIED;
  readonly properties: {
    /**
     * Name of form field
     */
    field: string;
  };
  readonly experimentId?: string;

  constructor(field: string) {
    this.properties = { field };
  }
}

/**
 * User has edited a form field
 */
export class FieldDirtiedEvent {
  readonly value = EventNames.FIELD_DIRTIED;
  readonly properties: {
    /**
     * Name of form field
     */
    formName: string;
    field: string;
  };
  readonly experimentId?: string;

  constructor(formName: string, field: string) {
    this.properties = { formName, field };
  }
}

/**
 * User viewed a step of the signup form.
 */
export class SignupStepViewedEvent {
  readonly value = EventNames.SSU_STEP_VIEWED;
  readonly properties: {
    /**
     * Name of step
     */
    step: SIGNUP_STEPS;
  };
  readonly experimentId?: string;
  constructor(step: SIGNUP_STEPS) {
    this.properties = { step };
  }
}

/**
 * User completed a step of the signup form
 * A more generic version of "SignupUserInfoSubmittedEvent"
 * as we plan to have more steps in the ssu flow in the near future.
 */
export class SignupStepSubmittedEvent {
  readonly value = EventNames.SSU_STEP_SUBMITTED;
  readonly properties: {
    /**
     * Name of step
     */
    step: SIGNUP_STEPS;
    /**
     * value of form field
     */
    submitValue: any;
  };
  readonly experimentId?: string;
  constructor(step: SIGNUP_STEPS, submitValue?: any) {
    this.properties = { step, submitValue };
  }
}

/**
 * Search query for Industry sent to server
 */
export class IndustrySearchSent {
  readonly value = EventNames.INDUSTRY_SEARCH_SENT;
  readonly properties: {
    /**
     * Search term entered by user
     */
    searchTerm: string;
  };
  readonly experimentId?: string;
  constructor(searchTerm: string) {
    this.properties = { searchTerm };
  }
}

/**
 * Event triggered when the result of a search is displayed to the user.
 *
 * Caveats:
 * - Can be fewer than SearchSent, since search results won't display if the user has searched a new term
 * - If numResults=0, will continue to display the previous search results instead
 */
export class IndustrySearchCompleted {
  readonly value = EventNames.INDUSTRY_SEARCH_COMPLETED;
  readonly properties: {
    /**
     * Number of search results
     */
    numResults: number;

    /**
     * The search term these results are for
     */
    searchTerm: string;
  };
  readonly experimentId?: string;
  constructor(numResults: number, searchTerm: string) {
    this.properties = { numResults, searchTerm };
  }
}

/**
 * Event triggered when a search result is selected
 */
export class IndustrySearchSelected {
  readonly value = EventNames.INDUSTRY_SEARCH_SELECTED;
  readonly properties: {
    /**
     * Internal name of selected industry
     */
    industryName: string;
  };
  readonly experimentId?: string;
  constructor(industryName: string) {
    this.properties = { industryName };
  }
}

/**
 * Event triggered when the user clicks "Continue" or "I don't see my business type"
 */
export class IndustryFormSubmitted {
  readonly value = EventNames.INDUSTRY_FORM_SUBMITTED;
  readonly properties: {
    /**
     * Internal name of submitted industry
     */
    industryName: string;
  };
  readonly experimentId?: string;
  constructor(industryName: string) {
    this.properties = { industryName };
  }
}

/**
 * User completed first half of the signup form
 */
export class SignupUserInfoSubmittedEvent {
  readonly value = EventNames.SIGNUP_USER_INFO_SUBMITTED;
  readonly properties = {};
  readonly experimentId?: string;
}
/**
 * User completed the signup form, most important conversion
 * metric for Webstie - we use this event to track a user signing up.
 */
export class UserSoftSignupEvent {
  readonly value = EventNames.SOFT_SIGNUP;
  readonly properties = {};
  readonly experimentId?: string;
  constructor(properties: SignedupUserProperties) {
    this.properties = properties;
  }
}
export class UserEarlyAccSoftSignupEvent {
  readonly value = EventNames.EARLY_ACCOUNT_SIGNUP;
  readonly properties = {};
  readonly experimentId?: string;
  constructor(properties: SignedupUserProperties) {
    this.properties = properties;
  }
}

/**
 * Requested page url could not be found
 */
export class PageNotFoundEvent {
  readonly value = EventNames.PAGE_NOT_FOUND;
  readonly properties: {
    /**
     * The missing url
     */
    href: string;
    /**
     * The url that a user was on prior
     */
    referrer: string;
  };
  readonly experimentId?: string;

  constructor(href: string, referrer: string) {
    this.properties = {
      href,
      referrer,
    };
  }
}

interface ExperimentViewedProps {
  variantId: string | boolean;
  experimentId: string;
  experimentTool: string;
  leadToken?: string;
  experimentType?: string;
}
/**
 * User is shown an experiment
 */
export class ExperimentViewedEvent {
  readonly value = EventNames.EXPERIMENT_VIEWED;
  readonly properties = {};
  readonly experimentId?: string;

  constructor({
    variantId,
    experimentId,
    experimentTool,
    leadToken,
    experimentType,
  }: ExperimentViewedProps) {
    this.properties = {
      variantId,
      experimentId,
      experimentTool,
      experimentType,
      leadToken,
    };
  }
}

/**
 * User has scrolled to an Experiment Module
 */
export class ExperimentModuleInViewEvent {
  readonly value = EventNames.EXPERIMENT_MODULE_IN_VIEW;
  readonly properties: {
    variantId: string | boolean;
    experimentId: string;
  };
  readonly experimentId?: string;

  constructor(variantId: string | boolean, experimentId: string) {
    this.properties = {
      variantId,
      experimentId,
    };
  }
}

/**
 * Dashboard Onboarding User is shown an experiment - for use with Onboarding Analytics Template
 */
export class OnboardingExperimentViewedEvent {
  readonly value = EventNames.ONBOARDING_EXPERIMENT_VIEWED;
  readonly properties: {
    variant: string | boolean;
    experiment: string;
  };
  readonly experimentId?: string;

  constructor(variant: string | boolean, experiment: string) {
    this.properties = {
      variant,
      experiment,
    };
  }
}

/**
 * User views footer component
 */
export class FooterViewedEvent {
  readonly value = EventNames.FOOTER_VIEWED;
  readonly properties: {
    target: string;
    component: string;
    path: string;
    search: string;
  };
  readonly experimentId?: string;

  constructor(path: string, search: string) {
    this.properties = {
      target: "footer.viewed",
      component: "footer",
      path,
      search,
    };
  }
}

/**
 * Vital stats on browser/site performance automatically reported through the app lifecycle
 */
export class MetricEvent {
  readonly value = EventNames.WEB_VITALS;

  readonly experimentId?: string;

  constructor(
    public readonly properties: {
      fid: number;
      ttfb: number;
      lcp: number;
      cls: number;
      fcp: number;
    },
  ) {
    return;
  }
}

/**
 * Use when you need to provide an event but don't want it tracked
 */
export class NullEvent {
  readonly value = EventNames.NONE;
  readonly properties = {};
  readonly experimentId?: string;
}

/**
 * Announcement banner dismissed
 */
export class AnnouncementBannerDismissedEvent {
  readonly value = EventNames.ANNOUNCEMENT_BANNER_DISMISSED;
  readonly properties = {};
  readonly experimentId?: string;
}
/**
 * Announcement banner shown
 */
export class AnnouncementBannerShownEvent {
  readonly value = EventNames.ANNOUNCEMENT_BANNER_SHOWN;
  readonly properties = {};
  readonly experimentId?: string;
}

/**
 * Helpshift chatbot shown
 */
export class HelpshiftShownEvent {
  readonly value = EventNames.HELPSHIFT_OPENED;
  readonly properties = {};
  readonly experimentId?: string;
}

/**
 * Multiselect Investment Sources submitted
 */
export class InvestmentSourcesMultiEvent {
  readonly value = EventNames.INVESTMENT_SOURCE_MULTISELECT;
  readonly properties: {
    investmentSources: string[];
    mappedSingleSource: string;
  };
  readonly experimentId?: string;

  constructor(investmentSources: string[], mappedSingleSource: string) {
    this.properties = {
      investmentSources,
      mappedSingleSource,
    };
  }
}

/**
 * Signup Maintenance Page Shown
 */
export class MaintenanceScreenViewedEvent {
  readonly value = EventNames.MAINTENANCE_SCREEN_VIEWED;
  readonly properties = {};
  readonly experimentId?: string;
}

/**
 * Fired when the client navigates to a page
 */
export class PageViewedEvent {
  readonly value = EventNames.PAGE_VIEWED;
  readonly properties: {
    path: string;
    referrer: string;
    search: string;
    title: string;
    url: string;
  };
  readonly experimentId?: string;

  constructor(
    path: string,
    referrer: string,
    search: string,
    title: string,
    url: string,
  ) {
    this.properties = {
      path,
      referrer,
      search,
      title,
      url,
    };
  }
}

/**
 * User dragged or scrolled the carousel
 */
export class CarouselCardChanged {
  readonly value = EventNames.CAROUSEL_CARD_CHANGED;
  readonly experimentId?: string;

  constructor(
    // before Jan 2023, type of "scroll" was used for buttons
    // "drag" was used for dragging the container
    public readonly properties: {
      type: ScrollStyle;
      direction: DragDirection;
      position: number;
      nextPosition: number;
    },
  ) {
    return;
  }
}

/**
 * User selects tags or statuses from product log filter
 */
export class FilterSelected {
  readonly value = EventNames.FILTER_SELECTED;
  readonly experimentId?: string;

  constructor(
    public readonly properties: {
      type: "status" | "tag" | "search";
      text: string;
      style: "button" | "checkbox" | "clearButton";
    },
  ) {
    return;
  }
}

export class FilterCleared {
  readonly value = EventNames.FILTER_CLEARED;
  readonly experimentId?: string;
  readonly properties: {
    filter: string;
  };
  constructor() {
    this.properties = {
      filter: "product-log",
    };
  }
}

/**
 * Product Log
 */

export class ProductLogSearchQuery {
  readonly value = EventNames.PRODUCT_LOG_SEARCH_QUERY_CHANGED;
  readonly properties: {
    index: string;
    queryString: string;
  };
  readonly experimentId?: string;
  constructor(queryString: string) {
    this.properties = {
      index: "productLog",
      queryString,
    };
  }
}

export class ProductLogPinnedPostClicked {
  readonly value = EventNames.PRODUCT_LOG_PINNED_POST_CLICKED;
  readonly experimentId?: string;

  constructor(
    public readonly properties: {
      url: string;
    },
  ) {
    return;
  }
}

/**
Content hub search events
 */
export class ShowMoreArticlesClicked {
  readonly value = EventNames.SHOW_MORE_ARTICLES;
  readonly properties = {};
  readonly experimentId?: string;
}

export class ClickFilterFromResourcesPage {
  readonly value = EventNames.OPEN_SEARCH_RESULTS;
  readonly properties: {
    searchQuery: string;
    filters: string;
  };
  readonly experimentId?: string;

  constructor(searchState: Record<string, any>) {
    this.properties = {
      searchQuery: searchState.query,
      filters: JSON.stringify(searchState.refinementList),
    };
  }
}

export class OpenArticleFromSearchResults {
  readonly value = EventNames.OPEN_ARTICLE;
  readonly properties: {
    url: string;
  };
  readonly experimentId?: string;

  constructor(url: string) {
    this.properties = {
      url,
    };
  }
}

export class ToggledContentHubFilters {
  readonly value = EventNames.TOGGLED_FILTERS;
  readonly properties: {
    filters: string;
  };
  readonly experimentId?: string;

  constructor(filters: string) {
    this.properties = {
      filters,
    };
  }
}

export class NoResultsViewShown {
  readonly value = EventNames.NO_RESULTS;
  readonly properties: {
    searchQuery: string;
    filters: string;
  };
  readonly experimentId?: string;

  constructor(searchState: Record<string, any>) {
    this.properties = {
      searchQuery: searchState.query,
      filters: JSON.stringify(searchState.refinementList),
    };
  }
}

export class ProductLogPageNavigation {
  readonly value = EventNames.PRODUCT_LOG_PAGE_CHANGE;
  readonly properties: {
    index: string;
    newPage: number;
  };
  readonly experimentId?: string;
  constructor(newPage: number) {
    this.properties = {
      index: "productLog",
      newPage,
    };
  }
}
export class FormStepViewedEvent {
  readonly value = EventNames.FORM_STEP_VIEWED;
  readonly properties: {
    /**
     * Email of the user
     */
    email?: string;
    /**
     * Name of step
     */
    step: string;
    /**
     * Name of the form
     */
    form: string;
    /**
     * Name of the company
     */
    companyName?: LeadFormValues["companyName"];
    /**
     * Website of the company
     */
    website?: LeadFormValues["website"];
    /**
     * Sales volume of the company
     */
    monthlySales?: LeadFormValues["monthlySales"];
    /**
     * Employee count of the company
     */
    employeeCount?: LeadFormValues["employeeCount"];
    /**
     * Funding sources of the company
     */
    fundingSources?: LeadFormValues["fundingSources"];
  };
  readonly experimentId?: string;
  constructor(step: string, form: string, values?: LeadFormValues) {
    this.properties = {
      step,
      form,
      email: values?.workEmail,
      companyName: values?.companyName,
      website: values?.website,
      monthlySales: values?.monthlySales,
      employeeCount: values?.employeeCount,
      fundingSources: values?.fundingSources,
    };
  }
}
/**
 * Users have landed on the external event form
 */
export class ExternalEventPageViewed {
  readonly value = EventNames.EVENT_PAGE_VIEWED;
  readonly properties: {
    /**
     * Name of the event
     */
    salesRepRefCode: string;
  };
  readonly experimentId?: string;
  constructor(salesRepRefCode: string) {
    this.properties = { salesRepRefCode };
  }
}

/**
 * Events related to lead routing via vendor systems.
 * Lead routing is to match the lead to the right sales rep.
 */
export class LeadRoutingEvent {
  readonly value = EventNames.LEAD_ROUTING_EVENT;
  readonly properties: {
    /**
     * Name of the routing provider
     */
    provider: "Chili Piper" | "LeanData";
    /**
     * Type of the event
     */
    type: LeadRoutingEventType;
  };
  readonly experimentId?: string;
  constructor(
    provider: "Chili Piper" | "LeanData",
    type: LeadRoutingEventType,
  ) {
    this.properties = { provider, type };
  }
}

/**
 * User viewed a row in scrolling sub nav module
 */
export class ScrollingSubNavRowViewed {
  readonly value = EventNames.SCROLLING_SUBNAV_ROW_VIEWED;
  readonly properties: {
    /**
     * Name of step
     */
    title: string;
  };
  readonly experimentId?: string;
  constructor(title: string) {
    this.properties = { title };
  }
}

/**
 * One Click Sign In Events
 */
export class OneClickSignInOptInEvent {
  readonly value = EventNames.ONE_CLICK_SIGN_IN_OPT_IN;
  public readonly properties = {};
}

export class OneClickSignInOptOutEvent {
  readonly value = EventNames.ONE_CLICK_SIGN_IN_OPT_OUT;
  public readonly properties = {};
}

export class OneClickSignInRedirectEvent {
  readonly value = EventNames.ONE_CLICK_SIGN_IN_REDIRECT;
  public readonly properties = {};
}

export class OneClickSignInDialogDisplayedEvent {
  readonly value = EventNames.ONE_CLICK_SIGN_IN_DIALOG_DISPLAYED;
  public readonly properties = {};
}
